import { useConfigStore, useSecuritySetting } from "../store/store";
import { generateW3CId } from "@microsoft/applicationinsights-core-js";

export class ApiError extends Error {
    status: number;
    constructor(message: string, status: number) {
        super(message);
        this.status = status;
        this.name = "ApiError";
    }
}

class ApiService {
    private logResponse(response: any, url: string, method: string): void {
        console.debug(`[${method}] ${url}:`, response);
    }

    private async buildHeaders(): Promise<Headers> {
        const headers = new Headers();
        const store = useSecuritySetting.getState();
        const token = await store.getAccessToken();
        headers.append("Content-Type", "application/json");
        if (token !== "") headers.append("Authorization", `Bearer ${token}`);
        return headers;
    }

    private async buildHeaderForm(): Promise<Headers> {
        const headers = new Headers();
        const store = useSecuritySetting.getState();
        const token = await store.getAccessToken();
        if (token !== "") headers.append("Authorization", `Bearer ${token}`);
        return headers;
    }

    private resetTraceId(): void {
        const configStore = useConfigStore.getState();
        if (configStore.appInsights) {
            configStore.appInsights?.getTraceCtx()?.setTraceId(generateW3CId());
        }
    }

    private async parseOrThrow<T>(response: Response): Promise<T> {
        if (response.ok) {
            if (response.status === 204) {
                return {} as T;
            }
            return (await response.json()) as T;
        }

        let message = `${response.status} ${response.statusText}`;
        try {
            const body = await response.clone().json();
            if (body?.detail) {
                message = body.detail;
            } else if (body?.messages?.length) {
                message = body.messages[0];
            } else if (body?.message) {
                message = body.message;
            } else {
                message = JSON.stringify(body);
            }
        } catch {
            try {
                message = await response.clone().text();
            } catch {
                /* ignore */
            }
        }
        throw new ApiError(message, response.status);
    }

    public async post<T>(url: string, body: any): Promise<T> {
        this.resetTraceId();
        console.info("[POST]: ", url);
        console.debug("Body: ", body);
        const response = await fetch(url, {
            method: "POST",
            headers: await this.buildHeaders(),
            body: JSON.stringify(body)
        });
        this.logResponse(response, url, "POST");
        return this.parseOrThrow<T>(response);
    }

    public async postBlob(url: string, body: any): Promise<Blob> {
        this.resetTraceId();
        console.info("[POST] (blob): ", url);
        console.debug("Body: ", body);
        const response = await fetch(url, {
            method: "POST",
            headers: await this.buildHeaders(),
            body: JSON.stringify(body)
        });
        this.logResponse(response, url, "POST/BLOB");
        if (response.ok) {
            return await response.blob();
        }
        // error path: parse error and throw
        await this.parseOrThrow<any>(response); // will throw
        throw new Error("Unreachable");
    }

    public async put<T>(url: string, body: any): Promise<T> {
        this.resetTraceId();
        console.info("[PUT]: ", url);
        console.debug("Body: ", body);
        const response = await fetch(url, {
            method: "PUT",
            headers: await this.buildHeaders(),
            body: JSON.stringify(body)
        });
        this.logResponse(response, url, "PUT");
        return this.parseOrThrow<T>(response);
    }

    public async postForm<T>(url: string, body: FormData): Promise<T> {
        this.resetTraceId();
        const headers = await this.buildHeaderForm();
        const options = {
            method: "POST",
            headers: headers,
            body: body
        };
        const response = await fetch(url, options);
        this.logResponse(response, url, "POST/FORM");
        return this.parseOrThrow<T>(response);
    }

    public async postFormGetResponse(url: string, body: FormData): Promise<Response> {
        this.resetTraceId();
        const headers = await this.buildHeaderForm();
        const options = {
            method: "POST",
            headers: headers,
            body: body
        };
        const response = await fetch(url, options);
        this.logResponse(response, url, "POST/FORM");
        // Throws if error, else returns response
        await this.parseOrThrow<any>(response);
        return response;
    }

    public async get<T>(url: string): Promise<T> {
        this.resetTraceId();
        console.info("[GET]: ", url);
        const response = await fetch(url, {
            method: "GET",
            headers: await this.buildHeaders()
        });
        this.logResponse(response, url, "GET");
        return this.parseOrThrow<T>(response);
    }

    public async delete<T>(url: string, data: any = null): Promise<T> {
        this.resetTraceId();
        console.info("[DELETE]: ", url);
        const response = await fetch(url, {
            method: "DELETE",
            headers: await this.buildHeaders(),
            body: data ? JSON.stringify(data) : null
        });
        this.logResponse(response, url, "DELETE");
        return this.parseOrThrow<T>(response);
    }

    public async getBlob(url: string): Promise<Blob> {
        this.resetTraceId();
        console.info("[GET] (blob): ", url);
        const response = await fetch(url, {
            method: "GET",
            headers: await this.buildHeaders()
        });
        this.logResponse(response, url, "GET/BLOB");
        if (response.ok) {
            return await response.blob();
        }
        await this.parseOrThrow<any>(response); // will throw
        throw new Error("Unreachable");
    }
}
export default new ApiService();
